import React, { useState, useEffect } from 'react';
import { friendshipService, PendingFriendship } from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import './PendingRequests.css';
import toast from 'react-hot-toast';

interface PendingRequestsProps {
  onClose: () => void;
  onRequestResponded: () => void;
}

const PendingRequests: React.FC<PendingRequestsProps> = ({ onClose, onRequestResponded }) => {
  const { t } = useSafeTranslation();
  const [pendingRequests, setPendingRequests] = useState<PendingFriendship[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [responding, setResponding] = useState<{ friendshipId: number; accept: boolean } | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    checkAuthentication();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadPendingRequests();
    }
  }, [isAuthenticated]);

  const checkAuthentication = () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setIsAuthenticated(false);
      setLoading(false);
      setError(t('friends.loginRequiredForRequests', 'You need to be logged in to view your friendship requests.'));
      return;
    }
    setIsAuthenticated(true);
  };

  const loadPendingRequests = async () => {
    if (!isAuthenticated) {
      return;
    }

    try {
      setLoading(true);
      const requests = await friendshipService.getPendingRequests();
      setPendingRequests(requests);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError(t('friends.loginRequiredForRequests', 'You need to be logged in to view your friendship requests.'));
      } else {
                  setError(t('friends.errorLoadingRequests', 'Error loading friendship requests. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (friendshipId: number, accept: boolean) => {
    if (!isAuthenticated) {
      toast.error(t('friends.loginRequiredToRespond', 'You need to be logged in to respond to friend requests.'));
      return;
    }

    try {
      setResponding({ friendshipId, accept });
      await friendshipService.respondToFriendshipRequest(friendshipId, accept);
      
      // Remove the request from the list
      setPendingRequests(prev => prev.filter(req => req.id !== friendshipId));
      
      // Notify parent component
      onRequestResponded();
      
      // Show success message
      toast.success(
        accept
          ? t('friends.requestAccepted', 'Friend request accepted')
          : t('friends.requestRejected', 'Friend request rejected'),
      );
      
    } catch (err: any) {
      const message = err.response?.data?.message || 'Error responding to the request';
      toast.error(message);
    } finally {
      setResponding(null);
    }
  };

  if (loading) {
    return (
      <div className="pending-requests-overlay">
        <div className="pending-requests-modal">
          <div className="pending-requests-header">
            <h2>{t('friends.friendshipRequests', 'Friendship Requests')}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="pending-requests-content">
            <div className="loading-spinner">{t('common.loading', 'Loading...')}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="pending-requests-overlay">
        <div className="pending-requests-modal">
          <div className="pending-requests-header">
            <h2>{t('friends.friendshipRequests', 'Friendship Requests')}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="pending-requests-content">
            <div className="not-authenticated">
              <div className="not-authenticated-icon">🔒</div>
              <h3>{t('auth.loginRequired', 'Login required')}</h3>
              <p>{t('friends.loginRequiredForRequests', 'You need to be logged in to view your friendship requests.')}</p>
              <button onClick={onClose} className="close-modal-button">
                {t('common.close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pending-requests-overlay">
      <div className="pending-requests-modal">
        <div className="pending-requests-header">
          <h2>{t('friends.friendshipRequests', 'Friendship Requests')} ({pendingRequests.length})</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        <div className="pending-requests-content">
          {error && <div className="error-message">{error}</div>}
          
          {pendingRequests.length === 0 ? (
            <div className="no-requests">
              <div className="no-requests-icon">📬</div>
              <h3>{t('friends.noPendingRequests', 'No pending requests')}</h3>
              <p>{t('friends.noPendingRequestsMessage', 'You don\'t have any pending friendship requests.')}</p>
            </div>
          ) : (
            <div className="requests-list">
              {pendingRequests.map((request) => (
                <div key={request.id} className="request-item">
                  <div className="request-item-top">
                    <div className="request-info">
                      <div className="user-info">
                        <span className="user-name">
                          @{(request.requesterNickname || '').trim() || '—'}
                        </span>
                        <span className="user-level">
                          {t('user.level', 'Level')} {request.requesterLevel}
                        </span>
                      </div>
                    </div>
                    <div className="request-actions">
                      <button
                        type="button"
                        className="accept-button"
                        onClick={() => handleRespond(request.id, true)}
                        disabled={responding?.friendshipId === request.id}
                        aria-label={t('friends.accept', 'Accept')}
                        title={t('friends.accept', 'Accept')}
                      >
                        {responding?.friendshipId === request.id && responding.accept ? (
                          <span className="spinner-border spinner-border-sm text-light" role="status" aria-hidden />
                        ) : (
                          <i className="bi bi-check-lg" aria-hidden />
                        )}
                      </button>
                      <button
                        type="button"
                        className="reject-button"
                        onClick={() => handleRespond(request.id, false)}
                        disabled={responding?.friendshipId === request.id}
                        aria-label={t('friends.reject', 'Reject')}
                        title={t('friends.reject', 'Reject')}
                      >
                        {responding?.friendshipId === request.id && !responding.accept ? (
                          <span className="spinner-border spinner-border-sm text-light" role="status" aria-hidden />
                        ) : (
                          <i className="bi bi-x-lg" aria-hidden />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="request-date">
                    {new Date(request.createdAt).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingRequests; 